import React, { Suspense, useEffect, useState } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import PrivateRoute from './auth/PrivateRoute';
import { Error404Page, ForgotPassword, GoogleCallback, Login, Register, SetNewPassword, Unauthorized, VerifyEmail } from './main';
import { ContactUs, Home, HowItWorks, Locations, Profile, Rent, Reservations, TripDetails, Trips, Vehicle, VehicleDetails } from './pages';
import { Accounts, AdminProfile, Agents, Booking, Checkout, CheckoutDetail, Dashboard, Insurance, Tax, Users, Vehicles } from './admin';
import { ToastContainer } from 'react-toastify';
import SplashScreen from '../global/splashScreen';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css'


const App: React.FC = () => {
  const [isOnline, setOnline] = useState<boolean>(true);

  const updateNetworkStatus = () => {
    setOnline(navigator.onLine);
  };

  //   sometimes, the load event does not trigger on some browsers, that is why manually calling updateNetworkStatus on initial mount
  useEffect(() => {
    updateNetworkStatus();
  }, []);

  useEffect(() => {
    window.addEventListener("load", updateNetworkStatus);
    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);

    return () => {
      window.removeEventListener("load", updateNetworkStatus);
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);
    };
  }, [navigator.onLine]);

  if (!isOnline) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="max-w-md text-center p-6 bg-white rounded-lg shadow-lg">
          <div className="mb-6">
            <i className="fa-solid fa-wifi-slash text-6xl text-red-500"></i>
            <h1 className="text-3xl font-bold text-red-500 mb-2">You are Offline</h1>
            <p className="text-gray-600">
              It seems you have lost your internet connection. Please check your network settings and try again.
            </p>
          </div>
          <button
            onClick={updateNetworkStatus}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (

    <HashRouter>
      <Suspense fallback={<SplashScreen />}>
        <ToastContainer />
        <Routes>
          {/* Guest Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/set-new-password" element={<SetNewPassword />} />
          <Route path="/auth-success" element={<GoogleCallback />} />

          <Route path="/" element={<PrivateRoute element={<Home />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="vehicle" element={<PrivateRoute element={<Vehicle />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="vehicle/:id" element={<PrivateRoute element={<VehicleDetails />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="rent" element={<PrivateRoute element={<Rent />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="trips" element={<PrivateRoute element={<Trips />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="trip/:id" element={<PrivateRoute element={<TripDetails />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="how-it-works" element={<PrivateRoute element={<HowItWorks />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="reservations" element={<PrivateRoute element={<Reservations />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="locations" element={<PrivateRoute element={<Locations />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />
          <Route path="profile" element={<PrivateRoute element={<Profile />} roles={['SUPER_ADMIN', 'ADMIN', 'USER']} />} />
          <Route path="contact-us" element={<PrivateRoute element={<ContactUs />} roles={['SUPER_ADMIN', 'ADMIN', 'USER', 'GUEST']} />} />

          {/* Admin Routes */}
          <Route path="admin-profile" element={<PrivateRoute element={<AdminProfile />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path="vehicles" element={<PrivateRoute element={<Vehicles />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path="accounts" element={<PrivateRoute element={<Accounts />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='dashboard' element={<PrivateRoute element={<Dashboard />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='bookings' element={<PrivateRoute element={<Booking />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='insurance' element={<PrivateRoute element={<Insurance />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='tax' element={<PrivateRoute element={<Tax />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='agents' element={<PrivateRoute element={<Agents />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='users' element={<PrivateRoute element={<Users />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='checkout' element={<PrivateRoute element={<Checkout />} roles={['SUPER_ADMIN', 'ADMIN']} />} />
          <Route path='checkout/:id' element={<PrivateRoute element={<CheckoutDetail />} roles={['SUPER_ADMIN', 'ADMIN']} />} />


          {/* Common Routes */}
          <Route path="*" element={<Error404Page />} />
          <Route path="unauthorized" element={<Unauthorized />} />
        </Routes>
      </Suspense>
    </HashRouter>
  );
};

export default App;
